"use client";

import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
function LiveClock() {
  const [time, setTime] = useState<string>("");
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
      setTime(formatter.format(now));
    };
    updateClock();
    const interval = setInterval(updateClock, 60000);
    return () => clearInterval(interval);
  }, []);
  return <span className="text-base tracking-wide" style={{
    color: "hsl(240 5% 70%)"
  }}>
      {"Swindon — "}
      {time}
      {" GMT"}
    </span>;
}
const servicesLinks = [{
  label: "National SEO",
  href: "/national-seo"
}, {
  label: "International SEO",
  href: "/international-seo"
}, {
  label: "Local SEO",
  href: "/local-seo"
}, {
  label: "B2B SEO",
  href: "/b2b-seo-lead-generation"
}, {
  label: "Ecommerce SEO",
  href: "/ecommerce-seo"
}];
const firmLinks = [{
  label: "Case Studies",
  href: "/CaseStudiesPage"
}, {
  label: "Blog",
  href: "/BlogPage"
}, {
  label: "About Me",
  href: "/AboutMePage"
}, {
  label: "Contact",
  href: "/ContactPage"
}];
const legalLinks = [{
  label: "Privacy Policy",
  href: "#"
}, {
  label: "Terms of Service",
  href: "#"
}];
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const handleBackToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: shouldReduceMotion ? "auto" : "smooth"
    });
  };
  return <section data-section-id="30711" id="site-footer" data-nav="dark" className="dark bg-foreground text-background pt-32 pb-10 md:pt-44 md:pb-12 px-5 sm:px-8 md:px-16">
      <motion.div initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.5,
      ease: "easeOut"
    }} className="mx-auto max-w-[1280px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-background" style={{
            fontSize: "clamp(36px, 4.5vw, 58px)",
            lineHeight: 0.96,
            letterSpacing: "-0.028em",
            fontWeight: 400
          }}>
              {"SEO, "}
              <em className="italic">expertly</em>
              {"."}
            </h2>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-6 lg:gap-8">
            <div>
              <h3 className="mb-6 text-sm uppercase font-medium" style={{
              letterSpacing: "0.18em",
              color: "hsl(240 5% 70%)"
            }}>
                Services
              </h3>
              <ul className="space-y-3">
                {servicesLinks.map((item, i) => <li key={item.label} data-index={i}>
                    <Link to={item.href} className="text-background text-lg hover:text-accent transition-colors duration-200">
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="mb-6 text-sm uppercase font-medium" style={{
              letterSpacing: "0.18em",
              color: "hsl(240 5% 70%)"
            }}>
                Company
              </h3>
              <ul className="space-y-3">
                {firmLinks.map((item, i) => <li key={item.label} data-index={i}>
                    <Link to={item.href} className="text-background text-lg hover:text-accent transition-colors duration-200">
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="mb-6 text-sm uppercase font-medium" style={{
              letterSpacing: "0.18em",
              color: "hsl(240 5% 70%)"
            }}>
                Legal
              </h3>
              <ul className="space-y-3">
                {legalLinks.map((item, i) => <li key={item.label} data-index={i}>
                    <Link to={item.href} className="text-background text-lg hover:text-accent transition-colors duration-200">
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="mb-6 text-sm uppercase font-medium" style={{
              letterSpacing: "0.18em",
              color: "hsl(240 5% 70%)"
            }}>
                Contact
              </h3>
              <ul className="space-y-3">
                <li>
                  <a href="mailto:contact@ye-consultancy.com" className="text-background text-lg hover:text-accent transition-colors duration-200 block break-all sm:break-normal">
                    contact@ye-consultancy.com
                  </a>
                </li>
                <li>
                  <a href="tel:+447490984430" className="text-background text-lg hover:text-accent transition-colors duration-200 block">
                    +44 7490984430
                  </a>
                </li>
                <li className="text-background text-lg leading-relaxed">
                  Swindon, UK
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="w-full my-12" style={{
        height: "1px",
        backgroundColor: "hsl(240 6% 25%)"
      }} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="text-center md:text-left">
            <p className="text-base" style={{
            color: "hsl(240 5% 70%)",
            letterSpacing: "0.02em",
            lineHeight: 1.4
          }}>
              {"© YE Consultancy — Independent SEO Strategy. All rights reserved."}
            </p>
          </div>

          <div className="text-center">
            <LiveClock />
          </div>

          <div className="text-center md:text-right">
            <Button onClick={handleBackToTop} variant="outline" className="rounded-md border-background bg-transparent text-background hover:bg-background hover:text-foreground px-5 py-2 h-auto text-base" style={{
            borderWidth: "1px"
          }}>
              Back to top
              <ArrowUp className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </section>;
}